import {
  keepPreviousData,
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
  type UseQueryOptions,
} from '@tanstack/react-query';
import {
  tmsApi,
  type CopyOrderRequest,
  type CreateOrderRequest,
  type ImportOrdersRequest,
  type ImportOrdersResult,
  type Order,
  type OrdersPage,
  type OrdersPageParams,
  type OrderStatusValue,
  type PreviousOrdersPage,
  type PreviousOrdersParams,
  type UpdateOrderRequest,
} from '../client';

/**
 * Stable query-key factory for order-scoped caches. Keyed by `'tms'` +
 * entity to avoid collisions with `@my/directory-api` keys that may
 * live in the same QueryClient.
 */
export const orderKeys = {
  all: ['tms', 'order'] as const,
  byOrganisation: (organisationId: string) => ['tms', 'order', 'org', organisationId] as const,
  byId: (id: string) => ['tms', 'order', 'id', id] as const,
};

/**
 * Fetch every order for an organisation (newest first, per BE). Pass
 * `null` to keep the query disabled (e.g. while the org id is still
 * resolving from Auth0).
 */
/** Rows per page of the "Start from a previous order" list. */
export const PREVIOUS_ORDERS_PAGE_SIZE = 5;

/**
 * A customer's earlier orders, a page at a time, for New order's "Start from a
 * previous order" list. Call `fetchNextPage` as the list scrolls; `data.orders`
 * is every order loaded so far, in the server's order (latest run first), and
 * `data.totalCount` how many there are in all.
 *
 * <p>
 * The cargo and variant are part of the key, so picking a different cargo
 * starts again from page 1 rather than filtering pages already loaded. Kept
 * under `orderKeys.all` so anything that invalidates orders refreshes it.
 * </p>
 */
export function usePreviousOrders(params: PreviousOrdersParams | null) {
  return useInfiniteQuery<
    PreviousOrdersPage,
    Error,
    { orders: Order[]; totalCount: number },
    readonly unknown[],
    number
  >({
    queryKey: [
      ...orderKeys.all,
      'previous',
      params?.organisationId ?? '',
      params?.customerPartyId ?? '',
      params?.cargoDefinitionId ?? '',
      params?.cargoDefinitionVariantId ?? '',
    ],
    initialPageParam: 1,
    queryFn: ({ pageParam }) =>
      tmsApi.getPreviousOrders({
        ...params!,
        page: pageParam,
        pageSize: PREVIOUS_ORDERS_PAGE_SIZE,
      }),
    getNextPageParam: (last) =>
      last.page * last.pageSize < last.totalCount ? last.page + 1 : undefined,
    select: (data) => ({
      orders: data.pages.flatMap((p) => p.items),
      totalCount: data.pages[data.pages.length - 1]?.totalCount ?? 0,
    }),
    enabled: !!params?.organisationId && !!params.customerPartyId,
    staleTime: 30 * 1000,
  });
}

/** Rows per page of the Orders screen. */
export const ORDERS_PAGE_SIZE = 20;

/**
 * The Orders screen, 20 at a time: searched, filtered and sorted by the
 * server so the order holds across pages. Call `fetchNextPage` as the grid
 * scrolls; `data.orders` is every order loaded so far.
 *
 * <p>
 * Search, status, dates and sort are part of the key, so changing any of them starts
 * again from page 1 — the previous rows stay on screen until the new first
 * page arrives rather than flashing a spinner. Kept under
 * `orderKeys.byOrganisation` so every order mutation refreshes it.
 * </p>
 */
export function useOrdersPage(params: OrdersPageParams | null) {
  return useInfiniteQuery<
    OrdersPage,
    Error,
    {
      orders: Order[];
      totalCount: number;
      organisationTotal: number;
      countByStatus: Map<OrderStatusValue, number>;
    },
    readonly unknown[],
    number
  >({
    queryKey: [
      ...orderKeys.byOrganisation(params?.organisationId ?? ''),
      'paged',
      params?.search?.trim() ?? '',
      params?.status ?? '',
      params?.activeOnly ?? false,
      params?.from ?? '',
      params?.to ?? '',
      params?.sort ?? 'newest',
      params?.descending ?? false,
    ],
    initialPageParam: 1,
    queryFn: ({ pageParam }) =>
      tmsApi.getOrdersPage({ ...params!, page: pageParam, pageSize: ORDERS_PAGE_SIZE }),
    getNextPageParam: (last) =>
      last.page * last.pageSize < last.totalCount ? last.page + 1 : undefined,
    select: (data) => {
      const last = data.pages[data.pages.length - 1];
      return {
        orders: data.pages.flatMap((p) => p.items),
        totalCount: last?.totalCount ?? 0,
        organisationTotal: last?.organisationTotal ?? 0,
        countByStatus: new Map((last?.statusCounts ?? []).map((c) => [c.status, c.count])),
      };
    },
    placeholderData: keepPreviousData,
    enabled: !!params?.organisationId,
    staleTime: 30 * 1000,
  });
}

export function useOrders(
  organisationId: string | null,
  options?: Omit<UseQueryOptions<Order[], Error>, 'queryKey' | 'queryFn' | 'enabled'>,
) {
  return useQuery<Order[], Error>({
    queryKey: organisationId ? orderKeys.byOrganisation(organisationId) : orderKeys.all,
    queryFn: () => {
      if (!organisationId) throw new Error('No organisation id');
      return tmsApi.getOrders(organisationId);
    },
    enabled: !!organisationId,
    staleTime: 30 * 1000,
    ...options,
  });
}

/**
 * Fetch a single order by id, with the service-tag join hydrated. Pass
 * `null` to keep the query disabled. Used by the Order Details edit
 * mode to hydrate the form from the saved row.
 */
export function useOrder(
  id: string | null,
  options?: Omit<UseQueryOptions<Order, Error>, 'queryKey' | 'queryFn' | 'enabled'>,
) {
  return useQuery<Order, Error>({
    queryKey: id ? orderKeys.byId(id) : orderKeys.all,
    queryFn: () => {
      if (!id) throw new Error('No order id');
      return tmsApi.getOrder(id);
    },
    enabled: !!id,
    staleTime: 30 * 1000,
    ...options,
  });
}

/**
 * Mutation wrapper around POST `/tms/api/v1/orders`. Invalidates the
 * per-org list cache on success so a future Orders list view picks
 * up the new row.
 *
 * <p>
 * Returns the full created `Order` (not just the id) so the page can
 * hydrate its detail view without an extra GET.
 * </p>
 */
export function useCreateOrder() {
  const qc = useQueryClient();
  return useMutation<Order, Error, CreateOrderRequest>({
    mutationKey: [...orderKeys.all, 'create'],
    mutationFn: (body) => tmsApi.createOrder(body),
    onSuccess: (_order, vars) => {
      qc.invalidateQueries({
        queryKey: orderKeys.byOrganisation(vars.organisationId),
      });
    },
  });
}

/**
 * Mutation wrapper around POST `/tms/api/v1/orders/copy` — New order's "Use
 * this order". Refreshes the org's order list (and the previous-orders list,
 * which sits under `orderKeys.all`) on success.
 */
export function useCopyOrder() {
  const qc = useQueryClient();
  return useMutation<Order, Error, CopyOrderRequest>({
    mutationKey: [...orderKeys.all, 'copy'],
    mutationFn: (body) => tmsApi.copyOrder(body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: orderKeys.all });
    },
  });
}

/**
 * Mutation wrapper around POST `/tms/api/v1/orders/import` (bulk create).
 * Resolves with per-order outcomes (`results`) so the caller can render a
 * result table and retry only the failures. Invalidates the per-org list
 * cache on success so created orders appear in the Orders view.
 */
export function useImportOrders() {
  const qc = useQueryClient();
  return useMutation<ImportOrdersResult, Error, ImportOrdersRequest>({
    mutationKey: [...orderKeys.all, 'import'],
    mutationFn: (body) => tmsApi.importOrders(body),
    onSuccess: (_res, vars) => {
      qc.invalidateQueries({
        queryKey: orderKeys.byOrganisation(vars.organisationId),
      });
    },
  });
}

/**
 * Mutation wrapper around PUT `/tms/api/v1/orders`. The caller passes
 * `organisationId` alongside the body so we can invalidate the per-org
 * list cache without an extra round-trip — the wire body itself only
 * carries the order id. Also seeds the by-id cache with the returned
 * row so the edit page renders the new values immediately.
 */
export function useUpdateOrder() {
  const qc = useQueryClient();
  return useMutation<Order, Error, { organisationId: string; body: UpdateOrderRequest }>({
    mutationKey: [...orderKeys.all, 'update'],
    mutationFn: ({ body }) => tmsApi.updateOrder(body),
    onSuccess: (order, { organisationId }) => {
      qc.invalidateQueries({ queryKey: orderKeys.byOrganisation(organisationId) });
      qc.setQueryData(orderKeys.byId(order.id), order);
    },
  });
}

/**
 * Mutation wrapper around DELETE `/tms/api/v1/orders/{id}`. The caller
 * passes `organisationId` so we can invalidate the per-org list cache —
 * the URL only carries the order id. The by-id cache is removed too so
 * a stale "edit deleted order" route doesn't render phantom data.
 */
export function useDeleteOrder() {
  const qc = useQueryClient();
  return useMutation<unknown, Error, { organisationId: string; orderId: string }>({
    mutationKey: [...orderKeys.all, 'delete'],
    mutationFn: ({ orderId }) => tmsApi.deleteOrder(orderId),
    onSuccess: (_res, { organisationId, orderId }) => {
      qc.invalidateQueries({ queryKey: orderKeys.byOrganisation(organisationId) });
      qc.removeQueries({ queryKey: orderKeys.byId(orderId) });
    },
  });
}
